import React, { useState, useRef } from 'react'
import { File, Eye, Trash2, ArrowDownAZ, ArrowUpZA, Calendar, FileType, ViewIcon, Download } from 'lucide-react'
import { formatFileSize } from '../utils/fileHelpers'
import { useLocalStorage } from '../hooks/useLocalStorage'

interface ShareLink {
  id: string
  hash: string
  itemId: string
  itemName: string
  itemType: 'file' | 'folder'
  createdAt: string
  views: number
  downloads: number
}

interface ShareLinkTableProps {
  links: ShareLink[]
  onViewLink: (link: ShareLink) => void
  onDeleteLink: (linkId: string) => void
  onCopyLink: (hash: string) => void
  loading: boolean
}

type SortField = 'itemName' | 'itemType' | 'createdAt' | 'views' | 'downloads'
type SortDirection = 'asc' | 'desc'

const ShareLinkTable: React.FC<ShareLinkTableProps> = ({
  links,
  onViewLink,
  onDeleteLink,
  onCopyLink,
  loading
}) => {
  // Sorting state with localStorage persistence
  const [sortField, setSortField] = useLocalStorage<SortField>('shareLinkSortField', 'createdAt')
  const [sortDirection, setSortDirection] = useLocalStorage<SortDirection>('shareLinkSortDirection', 'desc')
  const [showSortMenu, setShowSortMenu] = useState(false)
  const sortMenuRef = useRef<HTMLDivElement | null>(null)
  
  // Custom folder icon with solid fill
  const FolderIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="20" 
      height="20" 
      viewBox="0 0 24 24" 
      fill="var(--color-secondary)" 
      stroke="var(--color-secondary)" 
      strokeWidth="0.5" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      className="mr-2"
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
    </svg>
  )
  
  // Handle sorting
  const handleSort = (field: SortField) => {
    if (field === sortField) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // Set new field and default to ascending
      setSortField(field)
      setSortDirection('asc')
    }
    setShowSortMenu(false)
  }
  
  // Sort the links
  const sortedLinks = [...links].sort((a, b) => {
    let comparison = 0
    
    switch (sortField) {
      case 'itemName':
        comparison = a.itemName.localeCompare(b.itemName)
        break
      case 'itemType':
        comparison = a.itemType.localeCompare(b.itemType)
        break
      case 'createdAt':
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        break
      case 'views':
        comparison = a.views - b.views
        break
      case 'downloads':
        comparison = a.downloads - b.downloads
        break
    }
    
    // Apply sort direction
    return sortDirection === 'asc' ? comparison : -comparison
  })
  
  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null
    
    return sortDirection === 'asc' 
      ? <ArrowDownAZ size={14} className="ml-1" /> 
      : <ArrowUpZA size={14} className="ml-1" />
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--color-primary)] mx-auto mb-4"></div>
        <p>Loading shared links...</p>
      </div>
    )
  }

  if (links.length === 0) {
    return (
      <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light p-8 text-center">
        <p className="text-[var(--color-accent)] mb-2">No shared links found</p>
        <p>You haven't shared any files or folders yet.</p>
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-[#2a2a2a] rounded-lg shadow-custom-light overflow-hidden">
      <div className="flex justify-between items-center p-4 border-b border-[var(--color-accent)] dark:border-[var(--color-secondary)] dark:border-opacity-30">
        <h2 className="text-xl font-semibold">Shared Links</h2>
        
        <div className="relative">
          <button
            className="btn btn-outline flex items-center text-sm py-1.5 px-3"
            onClick={() => setShowSortMenu(!showSortMenu)}
          >
            {sortDirection === 'asc' ? <ArrowDownAZ size={18} /> : <ArrowUpZA size={18} />}
            <span className="ml-2">Sort</span>
          </button>
          
          {showSortMenu && (
            <div 
              ref={sortMenuRef}
              className="absolute right-0 top-10 w-48 bg-white dark:bg-[#2a2a2a] shadow-custom-medium dark:shadow-lg dark:shadow-black/30 rounded-md z-50 border border-[var(--color-accent)] border-opacity-20 dark:border-[var(--color-secondary)] dark:border-opacity-30"
            >
              <div className="py-1">
                <button
                  className={`w-full text-left px-4 py-2 flex items-center ${
                    sortField === 'itemName' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                  }`}
                  onClick={() => handleSort('itemName')}
                >
                  <ArrowDownAZ size={16} className="mr-2" />
                  Name
                </button>
                <button
                  className={`w-full text-left px-4 py-2 flex items-center ${
                    sortField === 'itemType' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                  }`}
                  onClick={() => handleSort('itemType')}
                >
                  <FileType size={16} className="mr-2" />
                  Type
                </button>
                <button
                  className={`w-full text-left px-4 py-2 flex items-center ${
                    sortField === 'createdAt' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                  }`}
                  onClick={() => handleSort('createdAt')}
                >
                  <Calendar size={16} className="mr-2" />
                  Date Created
                </button>
                <button
                  className={`w-full text-left px-4 py-2 flex items-center ${
                    sortField === 'views' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                  }`}
                  onClick={() => handleSort('views')}
                >
                  <ViewIcon size={16} className="mr-2" />
                  Views
                </button>
                <button
                  className={`w-full text-left px-4 py-2 flex items-center ${
                    sortField === 'downloads' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                  }`}
                  onClick={() => handleSort('downloads')}
                >
                  <Download size={16} className="mr-2" />
                  Downloads
                </button>
              </div>
              <div className="border-t border-[var(--color-accent)] border-opacity-20 dark:border-[var(--color-secondary)] dark:border-opacity-30 py-1">
                <button
                  className={`w-full text-left px-4 py-2 flex items-center ${
                    sortDirection === 'asc' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                  }`}
                  onClick={() => setSortDirection('asc')}
                >
                  <ArrowDownAZ size={16} className="mr-2" />
                  Ascending
                </button>
                <button
                  className={`w-full text-left px-4 py-2 flex items-center ${
                    sortDirection === 'desc' ? 'bg-[var(--color-background)] dark:bg-[#1f1f1f] font-medium' : 'hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]'
                  }`}
                  onClick={() => setSortDirection('desc')}
                >
                  <ArrowUpZA size={16} className="mr-2" />
                  Descending
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="overflow-auto max-h-[calc(100vh-220px)]">
        <table className="min-w-full divide-y divide-[var(--color-accent)] dark:divide-[var(--color-secondary)] dark:divide-opacity-30">
          <thead className="bg-[var(--color-background)] dark:bg-[#1f1f1f] sticky top-0 z-10">
            <tr>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('itemName')}
              >
                <div className="flex items-center">
                  Item
                  {renderSortIndicator('itemName')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('itemType')}
              >
                <div className="flex items-center">
                  Type
                  {renderSortIndicator('itemType')}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                Share Link
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('createdAt')}
              >
                <div className="flex items-center">
                  Created
                  {renderSortIndicator('createdAt')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('views')}
              >
                <div className="flex items-center">
                  Views
                  {renderSortIndicator('views')}
                </div>
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('downloads')}
              >
                <div className="flex items-center">
                  Downloads
                  {renderSortIndicator('downloads')}
                </div>
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-[var(--color-primary)] uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-[#2a2a2a] divide-y divide-[var(--color-accent)] dark:divide-[var(--color-secondary)] dark:divide-opacity-30">
            {sortedLinks.map((link) => (
              <tr key={link.id} className="hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f]">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {link.itemType === 'folder' ? (
                      <FolderIcon />
                    ) : (
                      <File size={20} className="text-[var(--color-primary)] mr-2" />
                    )}
                    <span className="font-medium">{link.itemName}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {link.itemType === 'folder' ? 'Folder' : 'File'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <a
                      href={`/${link.hash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#4C704C] hover:underline"
                    >
                      /{link.hash}
                    </a>
                    <button
                      className="ml-2 p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                      onClick={() => onCopyLink(link.hash)}
                      title="Copy link"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {new Date(link.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {link.views}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {link.downloads}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <button
                      className="p-1 text-[var(--color-primary)] hover:bg-[var(--color-background)] dark:hover:bg-[#1f1f1f] rounded"
                      onClick={() => onViewLink(link)}
                      title="View"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                      onClick={() => onDeleteLink(link.id)}
                      title="Delete share link"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ShareLinkTable